import {
  X, Share2, Link, Trash2, Download, Copy, FolderInput, Edit3, ArrowUpDown, List as ListIcon, Info,
} from '../../icons';
import { Button } from '../../components/shared';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 선택 툴바 (2026-08-14) — 사용자 요구:
 *
 *   "파일을 체크하면 내 파일, 정렬이 있는 해당 줄에 공유, 링크복사, 삭제, 다운로드, 복제,
 *    다음으로 이동, 다음으로 복사, 이름바꾸기, 정렬, 몇개 선택, 보기, 세부정보가
 *    한줄로 보인다."
 *
 * 브레드크럼 줄을 **대체**한다(그 위에 겹치지 않는다) — 겹치면 좁은 폭에서 두 줄이 되어
 * 목록이 그만큼 밀린다.
 *
 * 여러 개를 골랐을 때 뜻이 흐려지는 항목이 있다: 이름 바꾸기는 하나에만 가능하고,
 * 링크 복사도 마찬가지다. 그런 항목은 **여러 개 선택 시 비활성**으로 둔다 — 감추면
 * 툴바가 선택 개수마다 다른 모양이 되어 버튼 위치가 흔들린다.
 */

export type BulkAction =
  | 'share' | 'copy-link' | 'delete' | 'download' | 'duplicate'
  | 'move' | 'copy-to' | 'rename' | 'detail';

interface Props {
  count: number;
  onClear: () => void;
  onAction: (action: BulkAction) => void;
  onSort: (anchor: HTMLElement) => void;
  onView: (anchor: HTMLElement) => void;
  detailOpen: boolean;
  /**
   * 이 위치를 바꿀 수 없는가 (2026-08-14, 기본 false) — 회사 공유 폴더의 일반 사원.
   *
   * 서버는 이미 403 으로 막고 있다. 여기서 비활성으로 두는 것은 **누르기 전에 알리기**
   * 위해서다. 감추지 않는 이유는 툴바가 선택 개수·위치마다 다른 모양이 되면 버튼 위치가
   * 흔들리기 때문(이 파일 상단 규약과 동일).
   */
  readOnly?: boolean;
}

export function WorkspaceSelectionBar({
  count, onClear, onAction, onSort, onView, detailOpen, readOnly = false,
}: Props) {
  /** 하나만 골랐을 때만 뜻이 서는 동작. */
  const single = count === 1;
  /** 쓰기가 필요한 동작 — 읽기 전용에서는 전부 잠근다. */
  const writable = !readOnly;

  const act = (
    key: BulkAction,
    Icon: typeof Share2,
    label: string,
    enabled = true,
    danger = false,
  ) => (
    /* 지우기는 정본 `quiet-danger` — 「평소엔 담담하고 손이 닿을 때 붉어진다」. */
    <Button
      key={key}
      variant={danger ? 'quiet-danger' : 'ghost'}
      size="compact"
      disabled={!enabled}
      onClick={() => onAction(key)}
      iconLeft={<Icon size={14} aria-hidden />}
    >
      {label}
    </Button>
  );

  return (
    <div className="ws-selbar" role="toolbar" aria-label={tWorkspace('workspace.sel.aria')}>
      <Button variant="ghost" size="sm" iconOnly
        onClick={onClear} aria-label={tWorkspace('workspace.sel.clear')}
        iconLeft={<X size={15} aria-hidden />}
      />
      <span className="ws-selbar-count ws-num">
        {tWorkspace('workspace.sel.count', { count })}
      </span>

      {act('share', Share2, tWorkspace('workspace.row.share'), single && writable)}
      {act('copy-link', Link, tWorkspace('workspace.row.copy_link'), single)}
      {act('download', Download, tWorkspace('workspace.row.download'))}
      {act('duplicate', Copy, tWorkspace('workspace.row.duplicate'), writable)}
      {act('move', FolderInput, tWorkspace('workspace.row.move'), writable)}
      {/* '다음으로 복사' 는 **내 드라이브 쪽에 쓴다** — 읽기 전용 위치에서도 정당하다. */}
      {act('copy-to', Copy, tWorkspace('workspace.row.copy_to'))}
      {act('rename', Edit3, tWorkspace('workspace.row.rename'), single && writable)}
      {act('delete', Trash2, tWorkspace('workspace.row.delete'), writable, true)}

      <span className="ws-selbar-sp" />

      <Button variant="ghost" size="compact"
        onClick={(e) => onSort(e.currentTarget)}
        iconLeft={<ArrowUpDown size={14} aria-hidden />}
      >
        {tWorkspace('workspace.toolbar.sort')}
      </Button>
      <Button variant="ghost" size="compact"
        onClick={(e) => onView(e.currentTarget)}
        iconLeft={<ListIcon size={14} aria-hidden />}
      >
        {tWorkspace('workspace.toolbar.view')}
      </Button>
      {/*
        ★ 세부정보는 **켜짐이 보여야 하는 단추**다 — 정본에 「눌린 도구 단추」 변형이 없어
          켜졌을 때만 `ws-selbar-btn--on` 을 덧붙인다. 그 클래스는 **테두리색과 잉크만** 준다.
      */}
      <Button
        variant="ghost"
        size="compact"
        className={detailOpen ? 'ws-selbar-btn--on' : undefined}
        aria-pressed={detailOpen}
        onClick={() => onAction('detail')}
        iconLeft={<Info size={14} aria-hidden />}
      >
        {tWorkspace('workspace.toolbar.detail')}
      </Button>
    </div>
  );
}
